// ribbonUtils.js

(function (window) {
  window.ribbonUtils = {
    validateProperties: function (properties) {
      // Always required attributes
      const requiredAttributes = ["deliveryType", "productId", "variantId"];

      // Check for always required attributes
      const missingRequired = [];
      requiredAttributes.forEach((attr) => {
        if (!properties.hasOwnProperty(attr)) {
          missingRequired.push(attr);
        }
      });

      if (missingRequired.length > 0) {
        return `Inform the following properties ${missingRequired.join(", ")}`;
      }

      if (properties.wrappingVariantId && !properties.wrappingPrice) {
        return "If wrappingVariantId is provided, wrappingPrice must also be provided";
      }

      if (properties.wrappingPrice && isNaN(properties.wrappingPrice)) {
        return "wrappingPrice must be a valid decimal number";
      }

      // Check that required fields are not empty
      const errors = [];
      requiredAttributes.forEach((i) => {
        if (!properties[i]) {
          errors.push(i);
        }
      });

      if (errors.length > 0) {
        return `${errors.join(", ")} cannot be empty`;
      }

      const deliveryTypeOptions = [
        "physical_notification",
        "physical_no_notification",
        "email_sender_choice",
        "link_sender_choice",
        "sms_sender_choice",
        "email_recipient_choice",
        "link_recipient_choice",
        "sms_recipient_choice",
      ];
      if (!deliveryTypeOptions.includes(properties.deliveryType)) {
        return `Inform one of the options ${deliveryTypeOptions.join(", ")}`;
      }

      if (
        ["email_recipient_choice", "email_sender_choice"].includes(
          properties.deliveryType,
        )
      ) {
        if (!properties.recipientEmail || !properties.recipientName) {
          return "For the email delivery type option recipientEmail and recipientName are required";
        }
      }

      if (
        ["sms_recipient_choice", "sms_sender_choice"].includes(
          properties.deliveryType,
        ) &&
        !properties.recipientNumber
      ) {
        return "For the sms delivery type option recipientNumber is required";
      }

      // Only validate email if it's provided
      if (
        properties.recipientEmail &&
        !this.validateEmail(properties.recipientEmail)
      ) {
        return "Recipient Email is not valid";
      }

      // Only validate number if it's provided
      if (
        properties.recipientNumber &&
        !this.validatePhoneNumber(
          properties.recipientNumber,
          properties.phoneCountryCode,
        )
      ) {
        return "Recipient Number is not valid";
      }

      if (
        properties.quantity &&
        (isNaN(+properties.quantity) || +properties.quantity <= 0)
      ) {
        return "Quantity must be a valid number and greater than 0";
      }

      // Check if scheduledDeliveryTime is set and is not within the next two weeks
      if (
        properties.scheduledDeliveryTime !== undefined &&
        properties.scheduledDeliveryTime !== null
      ) {
        const twoWeeks = 14 * 24 * 60 * 60 * 1000;
        const now = new Date().getTime() - 1000;
        const delayDate = new Date(properties.scheduledDeliveryTime);

        if (isNaN(delayDate.getTime())) {
          return "Invalid scheduledDeliveryTime date.";
        }

        if (delayDate.getTime() < now || delayDate.getTime() > now + twoWeeks) {
          return "Invalid scheduledDeliveryTime date. It must be within the next two weeks.";
        }
      }

      return "";
    },
    addToCart: async function (
      formattedProperties,
      properties,
      useAlpineCheckout,
    ) {
      const addToCartUrl = "/cart/add.js";
      if (useAlpineCheckout && window.Alpine?.store?.("cart")?.addToCart) {
        await window.Alpine.store("cart").addToCart({
          id: properties.variantId,
          quantity: 1,
          properties: formattedProperties,
        });
      } else {
        console.log(
          "Alpine not available, falling back to default addProductToCart",
        );

        const response = await fetch(addToCartUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: properties.variantId,
            quantity: properties.quantity || 1,
            properties: formattedProperties,
          }),
        });
        const data = await response.json();
        console.log("Product added to cart:", data);

        if (typeof window?.ribbonCallbacks?.onAddToCart === "function") {
          window.ribbonCallbacks.onAddToCart();
        } else {
          const location = properties.redirectTo || "/cart";
          setTimeout(() => (window.location.href = location), 100);
        }
      }
    },
    addProductToGift: async function (properties, useAlpineCheckout) {
      const requiredAttributes = ["productId", "variantId"];

      if (
        properties.quantity &&
        (isNaN(+properties.quantity) || +properties.quantity <= 0)
      ) {
        throw new Error("Quantity must be a valid number and greater than 0");
      }

      const res = await fetch("/cart.js");
      const currentCart = await res.json();

      const giftItems = currentCart.items.filter(
        (item) => item.properties._physicalVariantId,
      );

      if (giftItems.length === 0) {
        throw new Error("No gift found in cart");
      }
      // Check for always required attributes
      const missingRequired = [];
      requiredAttributes.forEach((attr) => {
        if (!properties.hasOwnProperty(attr)) {
          missingRequired.push(attr);
        }
      });
      if (missingRequired.length > 0) {
        throw new Error(
          "Missing required attributes: " + missingRequired.join(", "),
        );
      }

      const formattedProperties = {
        _productId: properties.productId.toString(),
        _physicalVariantId: properties.variantId.toString(),
        _additionalProperties: properties.additionalProperties || [],
        "Send as Gift": "✓",
      };

      await this.addToCart(formattedProperties, properties, useAlpineCheckout);
    },
    createGift: async function (properties, useAlpineCheckout) {
      const validate = this.validateProperties(properties);
      if (validate) {
        throw new Error(validate);
      }

      const addToCartUrl = "/cart/add.js";
      try {
        // Get or create UUID and add it as a hidden attribute
        const customerUUID = this.getOrCreateCustomerUUID();
        const isMobile = this.isMobileDevice();

        await this.updateCartAttributes({
          _customer_uuid__: customerUUID,
          _isMobile__: isMobile,
          _giftAttributes: JSON.stringify({
            _deliveryType: properties.deliveryType,
            _wrappingGiftVariantId: (
              properties.wrappingVariantId || ""
            ).toString(),
            _wrappingPrice: +properties.wrappingPrice || 0,
            "Recipient Email": properties.recipientEmail || "",
            "Gift Message": properties.giftMessage || "",
            _recipientName: properties.recipientName || "",
            "Recipient Number": properties.recipientNumber || "",
            _scheduledDeliveryTime: properties.scheduledDeliveryTime || null,
            _uploaderyUrl: properties.uploaderyUrl || "",
          }),
        });

        const formattedProperties = {
          _productId: properties.productId.toString(),
          _physicalVariantId: properties.variantId.toString(),
          "Send as Gift": "✓",
          _deliveryType: properties.deliveryType,
          _recipientName: properties.recipientName,
          _additionalProperties: properties.additionalProperties || [],
        };

        if (useAlpineCheckout && window.Alpine?.store?.("cart")?.addToCart) {
          await window.Alpine.store("cart").addToCart({
            id: properties.variantId,
            quantity: properties.quantity || 1,
            properties: formattedProperties,
          });
        } else {
          console.log(
            "Alpine not available, falling back to default addProductToCart",
          );

          const res = await fetch("/cart.js");
          const currentCart = await res.json();

          const giftItems = currentCart.items.filter(
            (item) => item.properties._physicalVariantId,
          );

          if (giftItems.length > 0) {
            console.warn("replacing current gift from cart");
            await fetch("/cart/update.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                updates: {
                  [giftItems[0].properties._physicalVariantId]: 0,
                },
              }),
            });
          }

          const response = await fetch(addToCartUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              id: properties.variantId,
              quantity: properties.quantity || 1,
              properties: formattedProperties,
            }),
          });
          const data = await response.json();
          console.log("Product added to cart:", data);

          if (typeof window?.ribbonCallbacks?.onAddToCart === "function") {
            window.ribbonCallbacks.onAddToCart();
          } else {
            const location = properties.redirectTo || "/cart";
            setTimeout(() => (window.location.href = location), 100);
          }
        }
      } catch (error) {
        console.error("Error adding product to cart:", error);
      }
    },

    /**
     * Integrates gift attributes with the cart
     * @param {Object} properties - The gift properties
     * @param {string} properties.deliveryType - The type of delivery for the gift
     * @param {string} properties.recipientEmail - Email address of the gift recipient
     * @param {string|null} properties.recipientNumber - Phone number of the gift recipient
     * @param {string} properties.giftMessage - Message to include with the gift
     * @param {string} properties.recipientName - Name of the gift recipient
     * @param {string} properties.productId - The product Id
     * @param {string} properties.variantId - The product variant id
     * @param {string|number} properties.wrappingPrice - Gift wrapping price
     * @param {string|number} properties.wrappingVariantId - Gift wrapping variant id
     * @param {string|number} properties.scheduledDeliveryTime - Date to delay sending the gift - same format supported by the Date javascript class (must be within two weeks)
     * @param {string|number} properties.redirectTo - Redirect to a specific page after adding the gift to the cart, default to cart page
     * @param {boolean} useAlpineCheckout - Controls if alphine checkeout should be used or not
     * @returns {Promise<void>}
     */
    ribbonIntegration: async function (properties, useAlpineCheckout) {
      console.warn(
        "This function is deprecated, use createGift and addProductToGift instead",
      );
      const validate = this.validateProperties(properties);
      if (validate) {
        throw new Error(validate);
      }

      try {
        // Get or create UUID and add it as a hidden attribute
        const customerUUID = this.getOrCreateCustomerUUID();
        const isMobile = this.isMobileDevice();
        await this.updateCartAttributes({
          _customer_uuid__: customerUUID,
          _isMobile__: isMobile,
          _giftAttributes: JSON.stringify({
            _deliveryType: properties.deliveryType,
            _wrappingGiftVariantId: (
              properties.wrappingVariantId || ""
            ).toString(),
            _wrappingPrice: +properties.wrappingPrice || 0,
            "Recipient Email": properties.recipientEmail || "",
            "Gift Message": properties.giftMessage || "",
            _recipientName: properties.recipientName || "",
            "Recipient Number": properties.recipientNumber || "",
            _scheduledDeliveryTime: properties.scheduledDeliveryTime || null,
            _uploaderyUrl: properties.uploaderyUrl || "",
          }),
        });

        const formattedProperties = {
          _productId: properties.productId.toString(),
          _physicalVariantId: properties.variantId.toString(),
          "Send as Gift": "✓",
          _deliveryType: properties.deliveryType,
          _additionalProperties: properties.additionalProperties || [],
          _recipientName: properties.recipientName,
        };

        await this.addToCart(
          formattedProperties,
          properties,
          useAlpineCheckout,
        );
      } catch (error) {
        console.error("Error adding product to cart:", error);
      }
    },
    generateRandomCode: function () {
      return Math.random().toString(36).substring(2, 8).toUpperCase();
    },
    isMobileDevice: function () {
      const isMobile =
        // Check portrait mobile width
        window.matchMedia("(max-width: 768px)").matches ||
        // Check landscape mobile height
        (window.matchMedia("(max-height: 768px) and (orientation: landscape)")
          .matches &&
          // Ensure it's actually a touch device
          window.matchMedia("(pointer: coarse)").matches);
      return isMobile;
    },
    getOrCreateCustomerUUID: function () {
      const storageKey = "ribbon_customer_uuid";
      let uuid = localStorage.getItem(storageKey);
      if (!uuid) {
        uuid = crypto.randomUUID();
        localStorage.setItem(storageKey, uuid);
      }
      return uuid;
    },
    updateCartAttributes: async function (attributes) {
      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ attributes }),
        });
        return await response.json();
      } catch (error) {
        console.error("Error updating cart attributes:", error);
        throw error;
      }
    },
    validateEmail: function (email) {
      const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },

    validatePhoneNumber: function (phoneNumber, countryCode) {
      const parsedNumber = parsePhoneNumberFromString(phoneNumber, countryCode);
      return parsedNumber?.isValid() && countryCode === parsedNumber?.country;
    },
    isEmailOrPhone: function (userInput) {
      let inputType = "unknown";
      if (userInput.includes("@") || /[a-zA-Z]/.test(userInput)) {
        inputType = "email";
      } else if ((userInput.match(/[0-9]/g) || []).length >= 4) {
        inputType = "phone";
      }
      return inputType;
    },
    /**
     * Flexible phone validation - allows optional +, spaces, dashes, parentheses, periods
     * Must have at least 6 digits, no letters or invalid special characters
     */
    validatePhoneNumberBasic: function (phoneNumber) {
      // Check for invalid characters (letters or special chars other than +, -, (), space, .)
      if (/[a-zA-Z]/.test(phoneNumber)) {
        return false;
      }
      // Only allow digits and standard phone formatting characters
      if (!/^[\d\s.\-+()]+$/.test(phoneNumber)) {
        return false;
      }
      // Extract just the digits
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      return digitsOnly.length >= 6 && digitsOnly.length <= 15;
    },
  };
})(window);
